import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {Alert, Badge, Button, Container, Spinner} from 'react-bootstrap';
import {FaCalendarAlt, FaEye, FaPlus, FaUsers} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import CommonMetaTag from './common-meta-tag';
import {getPartnerOpenMatches} from '../api';
import {ROLES} from '../constants';

const statusKeys = {
  open: 'OPEN_MATCH_STATUS_OPEN',
  full: 'OPEN_MATCH_STATUS_FULL',
  confirmed: 'OPEN_MATCH_STATUS_CONFIRMED',
  completed: 'OPEN_MATCH_STATUS_COMPLETED',
  cancelled: 'OPEN_MATCH_STATUS_CANCELLED',
  expired: 'OPEN_MATCH_STATUS_EXPIRED'
};

const formatDateTime = (value, locale) => {
  if (!value) return '-';
  return new Date(value).toLocaleString(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PartnerOpenMatches = ({profile, token, history}) => {
  const {t, i18n} = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMatches = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const response = await getPartnerOpenMatches(token);
    if (response.status === 200) {
      setMatches(response.json.openMatches || []);
    } else {
      setError(t('PARTNER_OPEN_MATCH_LOAD_FAILED'));
    }
    setLoading(false);
  }, [t, token]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const sortedMatches = useMemo(() => [...matches].sort((first, second) => (
    new Date(second.startDatetime) - new Date(first.startDatetime)
  )), [matches]);

  if (!profile || !token) return <Redirect to="/login"/>;
  if (!profile.user || profile.user.role !== ROLES.partner) return <Redirect to="/"/>;
  if (!profile.openMatchesEnabled) return <Redirect to="/partner-fields"/>;

  return (
    <Container className="open-matches-page partner-open-matches-page">
      <CommonMetaTag/>
      <div className="open-matches-hero">
        <span><FaUsers aria-hidden="true"/> {t('PARTNER_OPEN_MATCH_EYEBROW')}</span>
        <h1>{t('OPEN_MATCHES')}</h1>
        <p>{t('PARTNER_OPEN_MATCH_PAGE_INTRO')}</p>
      </div>

      <div className="open-matches-toolbar partner-open-matches-toolbar">
        <Button className="open-matches-create-button" onClick={() => history.push('/partner-open-matches/create')}>
          <FaPlus aria-hidden="true"/> {t('CREATE_OPEN_MATCH')}
        </Button>
        <Button className="open-matches-refresh-button" onClick={loadMatches} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm"/> : <FaCalendarAlt aria-hidden="true"/>} {t('REFRESH')}
        </Button>
      </div>

      {error ? <Alert variant="warning">{error}</Alert> : null}
      {loading ? <div className="open-match-v1-loading"><Spinner animation="border"/>{t('LOADING')}</div> : null}
      {!loading && sortedMatches.length === 0 ? <div className="open-match-v1-empty">{t('PARTNER_OPEN_MATCH_EMPTY')}</div> : null}

      <div className="partner-open-matches-grid">
        {sortedMatches.map(openMatch => (
          <article key={openMatch.id} className="open-match-card partner-open-match-card">
            <div className="partner-open-match-card__header">
              <Badge>{t(statusKeys[openMatch.status] || 'OPEN_MATCH_STATUS_OPEN')}</Badge>
              <strong>{openMatch.playersJoined}/{openMatch.playersRequired}</strong>
            </div>
            <h2>{openMatch.title || openMatch.fieldName || t('OPEN_MATCHES')}</h2>
            <p>{openMatch.partnerName}</p>
            <div className="partner-open-match-card__meta">
              <span><FaCalendarAlt aria-hidden="true"/> {formatDateTime(openMatch.startDatetime, i18n.language || 'it-IT')}</span>
              <span>{openMatch.sport} · {openMatch.minLevel}-{openMatch.maxLevel}</span>
            </div>
            <Button className="open-match-v1-primary" onClick={() => history.push(`/matches/${openMatch.publicId}`)}>
              <FaEye aria-hidden="true"/> {t('VIEW_MATCH')}
            </Button>
          </article>
        ))}
      </div>
    </Container>
  );
};

PartnerOpenMatches.propTypes = {
  profile: PropTypes.object,
  token: PropTypes.string,
  history: PropTypes.object.isRequired
};

PartnerOpenMatches.defaultProps = {
  profile: null,
  token: null
};

const mapStateToProps = ({login: {profile, token}}) => ({profile, token});

export default connect(mapStateToProps)(PartnerOpenMatches);
